export interface CustomFieldEnumValue {
  value: string;
  valueDescription: string;
}

export interface CustomFieldDef {
  tag: number;
  tagDescription: string;
  dataType: string;
  enum?: CustomFieldEnumValue[];
}

export interface DataDictionaryFieldsIndex {
  tagToLabel: Map<number, string>;
  labelToTag: Map<string, number>;
  tagToEnumHint: Map<number, Map<string, string>>;
}

export type ItemRef =
  | { kind: "field"; name: string; required: boolean }
  | { kind: "component"; name: string; required: boolean }
  | { kind: "group"; group: GroupDef };

export interface GroupDef {
  name: string;
  required: boolean;
  items: ItemRef[];
}

export interface ComponentDef {
  name: string;
  items: ItemRef[];
}

export interface MessageDef {
  name: string;
  msgType: string;
  msgCat?: string;
  items: ItemRef[];
}

export type DataDictionarySource = "local" | "url";

export interface DataDictionary {
  id?: string;
  enabled: boolean;
  source?: DataDictionarySource;
  dataSource?: {
    filename: string;
    error?: string;
    warnings?: string[];
    content: string;
  };
  fields: CustomFieldDef[];
  fieldsIndex?: DataDictionaryFieldsIndex;
  components: ComponentDef[];
  componentsIndex?: Map<string, ComponentDef>;
  messages: MessageDef[];
  messagesByType?: Map<string, MessageDef>;
}

export const createDataDictionaryFieldsIndex = (fields: CustomFieldDef[]) => {
  const index: DataDictionaryFieldsIndex = {
    tagToLabel: new Map(),
    labelToTag: new Map(),
    tagToEnumHint: new Map(),
  };

  for (const field of fields) {
    index.tagToLabel.set(field.tag, field.tagDescription);
    index.labelToTag.set(field.tagDescription, field.tag);

    const enumHints = new Map();
    index.tagToEnumHint.set(field.tag, enumHints);
    if (field.enum) {
      for (const enumValue of field.enum) {
        enumHints.set(enumValue.value, enumValue.valueDescription);
      }
    }
  }
  return index;
};

export const createComponentsIndex = (
  components: ComponentDef[],
): Map<string, ComponentDef> => {
  const m = new Map<string, ComponentDef>();
  for (const c of components) m.set(c.name, c);
  return m;
};

export const createMessagesByType = (
  messages: MessageDef[],
): Map<string, MessageDef> => {
  const m = new Map<string, MessageDef>();
  for (const msg of messages) {
    if (msg.msgType) m.set(msg.msgType, msg);
  }
  return m;
};

export const newDataDictionary = (): DataDictionary => {
  return {
    enabled: false,
    fields: [],
    components: [],
    messages: [],
  };
};

export interface DataDictionaryParseResult {
  success: boolean;
  data?: {
    fields: CustomFieldDef[];
    components: ComponentDef[];
    messages: MessageDef[];
    warnings: string[];
  };
  error?: {
    message: string;
  };
}

const newSuccessDataDictionaryParseResult = (
  fields: CustomFieldDef[],
  components: ComponentDef[],
  messages: MessageDef[],
  warnings: string[],
): DataDictionaryParseResult => {
  return {
    success: true,
    data: { fields, components, messages, warnings },
  };
};

const newFailDataDictionaryParseResult = (
  message: string,
): DataDictionaryParseResult => {
  return {
    success: false,
    error: { message },
  };
};

export const loadDictionary = (content: string): DataDictionaryParseResult => {
  try {
    return loadDataDictionaryInternal(content);
  } catch (error) {
    return newFailDataDictionaryParseResult(`${error}`);
  }
};

const parseRequiredAttr = (val: string | null | undefined): boolean => {
  return (val || "").toUpperCase() === "Y";
};

const childElements = (parent: Element): Element[] => {
  return Array.from(parent.children);
};

const formatDuplicates = (
  label: string,
  duplicates: (string | number)[],
): string => {
  const sample = duplicates.slice(0, 5).join(", ");
  const more =
    duplicates.length > 5 ? ` (and ${duplicates.length - 5} more)` : "";
  return `Duplicate ${label}: ${sample}${more}`;
};

// Recursively parse <field>/<component>/<group> children of an element into ItemRefs.
const parseItems = (parent: Element): ItemRef[] => {
  const items: ItemRef[] = [];
  for (const child of childElements(parent)) {
    const tag = child.tagName.toLowerCase();
    if (tag === "field") {
      const name = child.getAttribute("name") || "";
      if (!name) continue;
      items.push({
        kind: "field",
        name,
        required: parseRequiredAttr(child.getAttribute("required")),
      });
    } else if (tag === "component") {
      const name = child.getAttribute("name") || "";
      if (!name) continue;
      items.push({
        kind: "component",
        name,
        required: parseRequiredAttr(child.getAttribute("required")),
      });
    } else if (tag === "group") {
      const name = child.getAttribute("name") || "";
      if (!name) continue;
      const group: GroupDef = {
        name,
        required: parseRequiredAttr(child.getAttribute("required")),
        items: parseItems(child),
      };
      items.push({ kind: "group", group });
    }
  }
  return items;
};

const loadDataDictionaryInternal = (
  content: string,
): DataDictionaryParseResult => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "application/xml");

  // DOMParser embeds a <parsererror> element on malformed XML.
  const parserError = doc.getElementsByTagName("parsererror");
  if (parserError && parserError.length > 0) {
    return newFailDataDictionaryParseResult(
      "XML parse error: " + (parserError[0].textContent || "unknown"),
    );
  }

  const fieldsSections = doc.getElementsByTagName("fields");
  if (!fieldsSections || fieldsSections.length === 0) {
    return newFailDataDictionaryParseResult("No fields section");
  }

  const resultFields: CustomFieldDef[] = [];
  const seenTags = new Map<number, string>(); // tag -> first name
  const seenNames = new Map<string, number>(); // name -> first tag
  const duplicateTags: number[] = [];
  const duplicateNames: string[] = [];
  const warnings: string[] = [];

  const fieldsSection = fieldsSections[0];
  for (const fieldEl of childElements(fieldsSection)) {
    if (fieldEl.tagName.toLowerCase() !== "field") continue;
    const numAttr = fieldEl.getAttribute("number");
    if (!numAttr) continue;
    const tag = Number(numAttr);
    if (!Number.isFinite(tag)) continue;

    const name = fieldEl.getAttribute("name") || "";
    const type = fieldEl.getAttribute("type") || "";

    // Keep first definition; record subsequent redefinitions as warnings so
    // vendor dictionaries with surprising duplicates still load.
    const tagAlreadySeen = seenTags.has(tag);
    const nameAlreadySeen = !!name && seenNames.has(name);
    if (tagAlreadySeen) duplicateTags.push(tag);
    if (nameAlreadySeen) duplicateNames.push(name);
    if (tagAlreadySeen || nameAlreadySeen) continue;
    seenTags.set(tag, name);
    if (name) seenNames.set(name, tag);

    const enumValues: CustomFieldEnumValue[] = [];
    for (const v of childElements(fieldEl)) {
      if (v.tagName.toLowerCase() !== "value") continue;
      const value = v.getAttribute("enum");
      const descRaw = v.getAttribute("description");
      const desc = descRaw != null ? String(descRaw) : "";
      if (value) enumValues.push({ value, valueDescription: desc });
    }

    const def: CustomFieldDef = {
      tag,
      tagDescription: name,
      dataType: type,
      ...(enumValues.length > 0 ? { enum: enumValues } : {}),
    };

    resultFields.push(def);
  }

  if (duplicateTags.length > 0) {
    warnings.push(formatDuplicates("field number(s)", duplicateTags));
  }
  if (duplicateNames.length > 0) {
    warnings.push(formatDuplicates("field name(s)", duplicateNames));
  }

  // Components
  const resultComponents: ComponentDef[] = [];
  const componentsSections = doc.getElementsByTagName("components");
  if (componentsSections && componentsSections.length > 0) {
    const componentsSection = componentsSections[0];
    for (const compEl of childElements(componentsSection)) {
      if (compEl.tagName.toLowerCase() !== "component") continue;
      const compName = compEl.getAttribute("name") || "";
      if (!compName) continue;
      resultComponents.push({ name: compName, items: parseItems(compEl) });
    }
  }

  // Messages
  const resultMessages: MessageDef[] = [];
  const messagesSections = doc.getElementsByTagName("messages");
  if (messagesSections && messagesSections.length > 0) {
    const messagesSection = messagesSections[0];
    for (const msgEl of childElements(messagesSection)) {
      if (msgEl.tagName.toLowerCase() !== "message") continue;
      const name = msgEl.getAttribute("name") || "";
      const msgType = msgEl.getAttribute("msgtype") || "";
      const msgCatAttr = msgEl.getAttribute("msgcat");
      if (!msgType) continue;
      const md: MessageDef = {
        name,
        msgType,
        items: parseItems(msgEl),
        ...(msgCatAttr != null ? { msgCat: msgCatAttr } : {}),
      };
      resultMessages.push(md);
    }
  }

  return newSuccessDataDictionaryParseResult(
    resultFields,
    resultComponents,
    resultMessages,
    warnings,
  );
};
